import { RulebookPage } from "@/components/RulebookPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { currentRules } from "@/data/rules";
import { DescriptionBlocks } from "./DescriptionBlocks";
import type { SpellScaling } from "@/data/types";

function scalingLabel(scaling: SpellScaling): string {
  switch (scaling) {
    case "multicast":
      return "Multicast";
    case "stacking":
      return "Stacking";
    case "binary":
      return "Binary";
  }
}

function scalingVariant(
  scaling: SpellScaling
): "default" | "secondary" | "outline" {
  switch (scaling) {
    case "multicast":
      return "default";
    case "stacking":
      return "secondary";
    case "binary":
      return "outline";
  }
}

export function Spellcasting() {
  return (
    <RulebookPage title="Spellcasting">
      <p className="text-sm text-muted-foreground mb-3">
        All spells require a free hand, to point at the target or throw a
        LARP-safe spell packet, and for the caster to clearly call out the name
        of the spell.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Every spell costs 1 MP to cast, except <em>Resist</em>, which costs 2.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Many of these spells allow you to level them up, allowing you to cast
        them multiple times quickly. If you have more than one high level spell,
        you can cast them without resetting their respective timers. For
        example, you could alternate casting several Blasts and Knockbacks.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Spells learned by Warlocks reach their maximum possible level at 8.
        Ultimate Warlocks do not have this restriction.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-3">Scaling</h2>
      <p className="text-sm text-muted-foreground mb-2">
        Extra spell points invested in a spell improve it in one of three ways:
      </p>
      <ul className="text-sm text-muted-foreground list-disc list-inside mb-6 space-y-1">
        <li>
          <strong className="text-foreground">Binary</strong> — extra points
          have no effect; the spell either works or it doesn&apos;t.
        </li>
        <li>
          <strong className="text-foreground">Multicast</strong> — each
          additional point allows the spell to be cast again within the duration
          for no extra MP cost.
        </li>
        <li>
          <strong className="text-foreground">Stacking</strong> — the spell
          effect compounds with each additional point invested.
        </li>
      </ul>

      {currentRules.spellLists.map((list, i) => (
        <div key={list.id}>
          {i > 0 && <hr className="my-6 border-border" />}
          <h2 className="text-xl font-semibold mt-6 mb-2 flex items-center gap-2">
            {list.name}{" "}
            <Badge variant="outline" className="text-xs">
              {list.id
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (s) => s.toUpperCase())
                .trim()}
            </Badge>
          </h2>
          {list.spells
            .filter((s) => !s.deprecated)
            .map((spell) => (
              <Card key={spell.id} className="mb-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex flex-wrap items-center gap-2">
                    {spell.name}
                    <Badge variant="outline" className="text-xs">
                      ⏱ {spell.duration}
                    </Badge>
                    <Badge
                      variant={scalingVariant(spell.scaling)}
                      className="text-xs"
                    >
                      {scalingLabel(spell.scaling)}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-1">
                  {spell.full_description ? (
                    <DescriptionBlocks blocks={spell.full_description} />
                  ) : (
                    <>
                      <p>{spell.effect}</p>
                      {spell.scalingDescription && (
                        <p className="italic">{spell.scalingDescription}</p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          {list.notes?.map((note, ni) => (
            <p key={ni} className="text-sm text-muted-foreground mt-2 mb-3">
              {note}
            </p>
          ))}
        </div>
      ))}
    </RulebookPage>
  );
}
